import { Component, Input, OnInit } from '@angular/core';
import {
  StockWebhookEventType,
  StockWebhookEventTypeLabel,
  StockWebhookHistoryFilter,
  StockWebhookHistoryItem,
} from '@app-model/vehicle';
import { OemDealerService } from '@app-services/oem-dealer.service';
import { UserInfo } from '@app/store/session/session.store';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-webhook-history',
  templateUrl: './webhook-history.component.html',
  styleUrls: ['../integration-history/integration-history.component.scss', './webhook-history.component.scss']
})
export class WebhookHistoryComponent implements OnInit {
  @Input() items: StockWebhookHistoryItem[] = [];
  @Input() numHistoryItems = 0;
  @Input() vehicleId: string;
  @Input() userInfo: UserInfo;

  eventTypes = Object.keys(StockWebhookEventTypeLabel) as StockWebhookEventType[];
  selectedEvent: StockWebhookEventType | '' = '';
  selectedResult: 'ALL' | 'SUCCESS' | 'ERROR' = 'ALL';
  loading = false;
  loadingResendId: string | null;
  currentPage = 0;
  numberOfPages = 1;
  totalElements = 0;

  constructor(
    private readonly oemDealerService: OemDealerService,
    private readonly toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    this.items = this.items || [];
    this.totalElements = this.numHistoryItems || this.items.length || 0;
  }

  onClickFullHistory(): void {
    this.loadHistory(0);
  }

  onFilterChange(): void {
    this.loadHistory(0);
  }

  previousPage(): void {
    if (this.currentPage < 1) {
      return;
    }
    this.loadHistory(this.currentPage - 1);
  }

  nextPage(): void {
    if (this.currentPage + 1 >= this.numberOfPages) {
      return;
    }
    this.loadHistory(this.currentPage + 1);
  }

  resend(item: StockWebhookHistoryItem): void {
    if (item.success || this.loadingResendId) {
      return;
    }

    this.loadingResendId = item.id;
    this.oemDealerService.resendStockWebhookHistory(item.id).pipe(
      finalize(() => this.loadingResendId = null),
    ).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastr.success('Webhook reenviado com sucesso.');
        } else {
          this.toastr.warning('Webhook reenviado, mas o cliente retornou falha.');
        }
        this.loadHistory(this.currentPage);
      },
      error: () => this.toastr.error('Não foi possível reenviar o webhook.'),
    });
  }

  getEventLabel(event: StockWebhookEventType): string {
    return StockWebhookEventTypeLabel[event] || event;
  }

  getResultLabel(item: StockWebhookHistoryItem): string {
    if (item.success) {
      return 'Sucesso';
    }
    return item.httpStatus ? `Falha HTTP ${item.httpStatus}` : 'Falha sem resposta';
  }

  showDetailsLink(item: StockWebhookHistoryItem): boolean {
    return !!(item.webhookUrl || item.requestBody || item.responseBody || item.errorMessage);
  }

  openDetailsLink(item: StockWebhookHistoryItem): void {
    item.showingDetails = true;
  }

  closeDetailsLink(item: StockWebhookHistoryItem): void {
    item.showingDetails = false;
  }

  canViewWebhookHistory(): boolean {
    return this.userInfo?.canViewWebhookHistory() ?? false;
  }

  canShowMore(): boolean {
    return this.totalElements > this.items.length || this.numberOfPages > 1;
  }

  private loadHistory(page: number): void {
    if (!this.vehicleId || !this.canViewWebhookHistory()) {
      return;
    }

    this.loading = true;
    const filter = this.buildFilter(page);

    this.oemDealerService.getStockWebhookHistories(this.vehicleId, filter).pipe(
      finalize(() => this.loading = false),
    ).subscribe({
      next: (response) => {
        this.items = response.data || [];
        this.currentPage = response.actualPage || 0;
        this.numberOfPages = response.numberOfPages || 1;
        this.totalElements = response.totalElements || 0;
      },
      error: () => this.toastr.error('Não foi possível carregar o histórico de webhook.'),
    });
  }

  private buildFilter(page: number): StockWebhookHistoryFilter {
    const filter = new StockWebhookHistoryFilter();
    filter.pageControl.page = page;
    filter.pageControl.pageSize = 10;
    filter.pageControl.sortField = 'createdAt';
    filter.pageControl.sortDirection = 'DESC';

    if (this.selectedEvent) {
      filter.event = this.selectedEvent;
    }

    if (this.selectedResult !== 'ALL') {
      filter.success = this.selectedResult === 'SUCCESS';
    }

    return filter;
  }
}
